export interface ThrowVector {
  power: number;
  yaw: number;
  pitch: number;
}

export interface ThrowInputCallbacks {
  onAim: (v: ThrowVector | null) => void;
  onRelease: (v: ThrowVector) => void;
  isEnabled: () => boolean;
}

export class ThrowInput {
  private startX = 0;
  private startY = 0;
  private active = false;
  private currentPointerId: number | null = null;
  private host: HTMLElement;
  private cb: ThrowInputCallbacks;

  constructor(host: HTMLElement, cb: ThrowInputCallbacks) {
    this.host = host;
    this.cb = cb;
    host.addEventListener('pointerdown', this.handleDown);
    host.addEventListener('pointermove', this.handleMove);
    host.addEventListener('pointerup', this.handleUp);
    host.addEventListener('pointercancel', this.handleUp);
  }

  destroy(): void {
    this.host.removeEventListener('pointerdown', this.handleDown);
    this.host.removeEventListener('pointermove', this.handleMove);
    this.host.removeEventListener('pointerup', this.handleUp);
    this.host.removeEventListener('pointercancel', this.handleUp);
  }

  private toVector(dx: number, dy: number): ThrowVector {
    const sw = Math.max(window.innerWidth, 1);
    const sh = Math.max(window.innerHeight, 1);
    const nx = dx / sw;
    const ny = dy / sh;
    const dist = Math.sqrt(nx * nx + ny * ny);
    const power = Math.min(1, dist / 0.45);
    const yaw = nx * Math.PI * 0.6;
    const pitch = Math.min(1, Math.max(0, -ny)) * 0.95;
    return { power, yaw, pitch };
  }

  private handleDown = (e: PointerEvent): void => {
    if (!this.cb.isEnabled()) return;
    if (e.target instanceof HTMLElement && e.target.closest('.layer.ignore-aim')) return;
    this.active = true;
    this.currentPointerId = e.pointerId;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.host.setPointerCapture?.(e.pointerId);
  };

  private handleMove = (e: PointerEvent): void => {
    if (!this.active || this.currentPointerId !== e.pointerId) return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    if (Math.abs(dx) + Math.abs(dy) < 6) {
      this.cb.onAim(null);
      return;
    }
    this.cb.onAim(this.toVector(dx, dy));
  };

  private handleUp = (e: PointerEvent): void => {
    if (!this.active || this.currentPointerId !== e.pointerId) return;
    this.active = false;
    this.currentPointerId = null;
    this.host.releasePointerCapture?.(e.pointerId);
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    if (Math.abs(dx) + Math.abs(dy) < 14) {
      this.cb.onAim(null);
      return;
    }
    const v = this.toVector(dx, dy);
    this.cb.onAim(null);
    this.cb.onRelease(v);
  };
}
